import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Boxes, PencilIcon, Trash2Icon } from 'lucide-react';
import apiClient from '../Services/ApiClient';
import { IconActionButton, PageContainer } from '../Styles/AppStyles';
import { COLORS } from '../Styles/Colors';
import ConfirmationModal from '../Components/Modal/ConfirmationModal';
import { decode } from '../Services/DecodeToken';
import useGetCustomData from '../Hooks/useGetCustomData';
import { useSnackbar } from '../Context/SnackbarProvider';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { Header } from '../Components/Header/Header';
import { LocationSelect } from '../Components/Select/LocationSelect';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import { BinDetailsPanel, BinPanelMode, BinToggleCell } from '../Components/Table/BinDetailsPanel';
import { ItemType, ItemTypeChip, ItemTypeToggle, useItemTypeParam } from '../Components/ItemType/ItemType';

/**
 * Inventory → View Inventory.
 *
 * ABATIX and Non-ABATIX items in one list (GET /api/inventory/combined).
 * Each row keeps its `item_type`, and every action (save, bins, delete) is
 * sent to that type's existing endpoint, so the stored data and the rules
 * for each type are unchanged:
 *   - ABATIX:     QTY / Min / Max editable (not for consignment locations).
 *   - Non-ABATIX: also Part No., Description, Category, Price and UOM.
 */

interface InventoryRow {
  row_key: string;
  item_type: ItemType;
  source_id: number;
  product_id: number;
  ovid: number;
  product_code: string;
  description: string;
  category_id: number | null;
  category_name: string;
  price: number | string | null;
  sales_pricing_unit: string;
  quantity: number;
  quantity_minimum: number;
  quantity_maximum: number;
  reorder_quantity: number;
  location_name: string;
  CustomerName: string;
  consignment_flag: number;
  binlocation_flag: number;
}

type EditableField =
  | 'quantity' | 'quantity_minimum' | 'quantity_maximum'
  | 'product_code' | 'description' | 'price' | 'sales_pricing_unit'
  | 'category_id' | 'category_name';

/** Unsaved edits, keyed by row_key. The original row is kept so edits survive paging. */
type PendingEdits = Record<string, { row: InventoryRow; fields: Partial<Record<EditableField, any>> }>;

const SEARCH_FIELDS = ['product_code', 'description', 'category_name', 'sales_pricing_unit', 'location_name', 'CustomerName'];
const QTY_FIELDS: EditableField[] = ['quantity', 'quantity_minimum', 'quantity_maximum'];

const editableHeaderWrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  paddingTop: '12px',
};

const editableHeaderTitleRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '4px' };

const editableHeaderTitleStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#64748b',
  letterSpacing: '0.06em',
  lineHeight: 1,
};

const editableHeaderHintStyle: React.CSSProperties = {
  fontSize: '10px',
  color: COLORS.teal,
  cursor: 'help',
  lineHeight: 1,
};

const EditedDot = () => (
  <span style={{ fontSize: '8px', color: COLORS.teal, fontWeight: 'bold', flexShrink: 0 }}>●</span>
);

const isBlank = (v: unknown) => v === '' || v === null || v === undefined;

const formatPrice = (value: unknown) => {
  if (isBlank(value)) return '-';
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
};

const blockNonNumericKeys = (e: React.KeyboardEvent) => {
  if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();
};

const InventoryPage = () => {
  const token: any = decode();
  const roleLevel = Number(token?.roles?.level) || 0;
  const { showSnackbar } = useSnackbar();

  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [itemType, setItemType] = useItemTypeParam();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState<string>('product_code');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { columnSearches, setSearch, clearSearch } = useColumnSearches(SEARCH_FIELDS);

  const handleColumnSearch = (field: string, val: string) => { setSearch(field, val); setPage(0); };
  const handleClearColumnSearch = (field: string) => { clearSearch(field); setPage(0); };
  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    setSortField(field || 'product_code');
    setSortOrder(order ?? 'asc');
    setPage(0);
  };

  const baseUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (itemType !== 'all') params.set('type', itemType);
    if (selectedOvs) params.set('ovid', selectedOvs);
    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    params.set('sort_field', sortField);
    params.set('sort_order', sortOrder);
    return `/api/inventory/combined?${params.toString()}`;
  }, [itemType, selectedOvs, columnSearches, sortField, sortOrder]);

  const paginatedUrl = `${baseUrl}&limit=${pageSize}&offset=${page * pageSize}`;
  const [inventory, loading, , refetch] = useGetCustomData(paginatedUrl);
  const rows: InventoryRow[] = useMemo(
    () => (Array.isArray(inventory?.data) ? inventory.data : []),
    [inventory],
  );
  const totalRows = Number(inventory?.totalCount ?? 0);

  // ─── Editing state ────────────────────────────────────────────────────────
  const [pending, setPending] = useState<PendingEdits>({});
  const [activeEditRowId, setActiveEditRowId] = useState<string | null>(null);
  const [activeCell, setActiveCell] = useState<{ key: string; field: EditableField } | null>(null);
  const [isEditAllMode, setIsEditAllMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryCache, setCategoryCache] = useState<Record<string, any[]>>({});
  const categoryRequests = useRef<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<InventoryRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedBinRowId, setExpandedBinRowId] = useState<string | null>(null);
  const [binPanelMode, setBinPanelMode] = useState<BinPanelMode>('view');

  const canEditBins = roleLevel >= 50;
  const defaultBinPanelMode: BinPanelMode = canEditBins ? 'manage' : 'view';

  // The expanded bin row belongs to the page/filter set it was opened on.
  useEffect(() => {
    setExpandedBinRowId(null);
    setBinPanelMode('view');
  }, [paginatedUrl]);

  const handleTypeChange = (next: typeof itemType) => {
    setItemType(next);
    setPage(0);
  };

  const toggleBinRow = (key: string, mode: BinPanelMode = defaultBinPanelMode) => {
    setExpandedBinRowId((prev) => (prev === key && binPanelMode === mode ? null : key));
    setBinPanelMode(mode);
  };

  // Same per-type rules as the two pages this replaces.
  const canEditQuantities = (row: InventoryRow) =>
    row.item_type === 'abatix' ? roleLevel >= 50 && !row.consignment_flag : true;
  const canEditDetails = (row: InventoryRow) => row.item_type === 'non-abatix';
  const canDelete = (row: InventoryRow) =>
    row.item_type === 'abatix' ? (!row.consignment_flag || roleLevel >= 100) : true;

  // Close an open text/category editor when clicking elsewhere.
  useEffect(() => {
    if (!activeCell) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-edit-toolbar-action]')) return;
      setActiveCell(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeCell]);

  // Load a location's Non-ABATIX categories the first time its category cell is edited.
  useEffect(() => {
    if (activeCell?.field !== 'category_id') return;
    const row = pending[activeCell.key]?.row ?? rows.find((r) => r.row_key === activeCell.key);
    const locationKey = row ? String(row.ovid) : '';
    if (!locationKey || categoryCache[locationKey] || categoryRequests.current.has(locationKey)) return;
    categoryRequests.current.add(locationKey);
    apiClient(`/api/non-abatix/categories?location_id=${locationKey}`)
      .then((response) => response.json())
      .then((categories) => setCategoryCache((prev) => ({ ...prev, [locationKey]: Array.isArray(categories) ? categories : [] })))
      .catch(() => setCategoryCache((prev) => ({ ...prev, [locationKey]: [] })));
  }, [activeCell, pending, rows, categoryCache]);

  const setField = (row: InventoryRow, field: EditableField, value: any) => {
    setPending((prev) => {
      const current = prev[row.row_key] ?? { row, fields: {} };
      return { ...prev, [row.row_key]: { row: current.row, fields: { ...current.fields, [field]: value } } };
    });
  };

  const valueOf = (row: InventoryRow, field: EditableField) => {
    const edited = pending[row.row_key]?.fields;
    return edited && field in edited ? edited[field] : (row as any)[field];
  };

  const isEdited = (row: InventoryRow, field: EditableField) => {
    const edited = pending[row.row_key]?.fields;
    return !!edited && field in edited && edited[field] !== (row as any)[field];
  };

  const resetEditing = () => {
    setPending({});
    setActiveEditRowId(null);
    setActiveCell(null);
    setIsEditAllMode(false);
  };

  const handleSaveChanges = async () => {
    const abatixPayload: any[] = [];
    const nonAbatixPayload: any[] = [];

    for (const { row, fields } of Object.values(pending)) {
      if (row.item_type === 'abatix') {
        abatixPayload.push({
          id: row.source_id,
          quantity: fields.quantity ?? row.quantity,
          quantity_minimum: fields.quantity_minimum ?? row.quantity_minimum,
          quantity_maximum: fields.quantity_maximum ?? row.quantity_maximum,
        });
      } else {
        const payload: Record<string, any> = {
          inventory_id: row.source_id,
          product_id: row.product_id,
          location_id: row.ovid,
        };
        if (fields.product_code !== undefined) payload.product_code = fields.product_code;
        if (fields.description !== undefined) payload.description = fields.description;
        if (fields.category_id !== undefined) payload.category_id = fields.category_id;
        if (fields.price !== undefined) payload.price = fields.price === '' ? null : Number(fields.price);
        if (fields.sales_pricing_unit !== undefined) {
          payload.unit_of_measure = fields.sales_pricing_unit === '' ? null : fields.sales_pricing_unit;
        }
        QTY_FIELDS.forEach((f) => {
          if (fields[f] !== undefined) payload[f] = Number(fields[f]);
        });
        nonAbatixPayload.push(payload);
      }
    }

    if (!abatixPayload.length && !nonAbatixPayload.length) return;

    const hasNegative = [...abatixPayload, ...nonAbatixPayload].some((item) =>
      QTY_FIELDS.some((f) => item[f] !== undefined && item[f] !== null && Number(item[f]) < 0),
    );
    if (hasNegative) {
      showSnackbar('Quantity on hand cannot be negative', 'error');
      return;
    }

    setSaving(true);
    const send = async (url: string, body: any[]) => {
      if (!body.length) return { ok: true, message: '' };
      try {
        const response = await apiClient(url, { method: 'PUT', body: JSON.stringify(body) });
        if (response.ok) return { ok: true, message: '' };
        const err = await response.json().catch(() => ({}));
        return { ok: false, message: err.error || err.message || 'Failed to save' };
      } catch (e: any) {
        return { ok: false, message: e?.message || 'Failed to save' };
      }
    };

    try {
      const [abatixResult, nonAbatixResult] = await Promise.all([
        send('/api/inventory/edit/bulk', abatixPayload),
        send('/api/non-abatix/inventory/edit/bulk', nonAbatixPayload),
      ]);

      if (abatixResult.ok && nonAbatixResult.ok) {
        showSnackbar('Changes saved successfully', 'success');
        resetEditing();
      } else {
        // Keep only the edits that did not save, so nothing is lost or saved twice.
        setPending((prev) => Object.fromEntries(
          Object.entries(prev).filter(([, { row }]) =>
            row.item_type === 'abatix' ? !abatixResult.ok : !nonAbatixResult.ok),
        ));
        const failed = [
          !abatixResult.ok && `ABATIX: ${abatixResult.message}`,
          !nonAbatixResult.ok && `Non-ABATIX: ${nonAbatixResult.message}`,
        ].filter(Boolean).join(' · ');
        const partly = (abatixPayload.length && abatixResult.ok) || (nonAbatixPayload.length && nonAbatixResult.ok);
        showSnackbar(partly ? `Some changes saved. Not saved — ${failed}` : failed, 'error');
      }
      refetch();
    } finally {
      setSaving(false);
    }
  };

  // ─── Cells ────────────────────────────────────────────────────────────────

  const renderQuantityCell = (row: InventoryRow, field: EditableField) => {
    const display = valueOf(row, field);
    const edited = isEdited(row, field);

    if (!canEditQuantities(row)) {
      return (
        <div
          style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', padding: '0 8px', cursor: 'default' }}
          title={row.consignment_flag ? 'Consignment location — quantities come from P21' : undefined}
        >
          <span>{isBlank(display) ? '-' : display}</span>
        </div>
      );
    }

    const isActive = isEditAllMode || activeEditRowId === row.row_key;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0 8px',
          backgroundColor: edited ? `${COLORS.teal}15` : 'transparent',
          cursor: 'pointer',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
        }}
        onClick={() => setActiveEditRowId(row.row_key)}
        onMouseEnter={(e) => { if (!isActive && !edited) e.currentTarget.style.backgroundColor = `${COLORS.teal}08`; }}
        onMouseLeave={(e) => { if (!isActive && !edited) e.currentTarget.style.backgroundColor = 'transparent'; }}
        title="Click to edit"
      >
        {isActive ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
            {edited && <EditedDot />}
            <input
              type="number"
              min={0}
              step={1}
              aria-label={`${field.replace('_', ' ')} for ${row.product_code}`}
              value={isBlank(display) ? '' : String(display)}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { setField(row, field, 0); return; }
                const parsed = Number(raw);
                if (!Number.isFinite(parsed)) return;
                setField(row, field, Math.max(0, parsed));
              }}
              autoFocus={!isEditAllMode && field === 'quantity'}
              style={{
                width: '100%',
                borderRadius: '4px',
                padding: '4px',
                outline: 'none',
                border: edited ? `1px solid ${COLORS.teal}` : 'none',
                backgroundColor: 'white',
              }}
              onKeyDown={(e) => {
                blockNonNumericKeys(e);
                if (e.key === 'Enter' || e.key === 'Escape') setActiveEditRowId(null);
              }}
            />
          </div>
        ) : (
          <span style={{ fontWeight: edited ? 'bold' : 'normal', color: edited ? COLORS.teal : 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {edited && <EditedDot />}
            {isBlank(display) ? '-' : display}
          </span>
        )}
      </div>
    );
  };

  /** Part No., Description, Price, UOM: click-to-edit on Non-ABATIX rows, plain text on ABATIX rows. */
  const renderDetailCell = (row: InventoryRow, field: EditableField, type: 'text' | 'number' = 'text') => {
    const display = valueOf(row, field);
    const shown = field === 'price' ? formatPrice(display) : (isBlank(display) ? '-' : display);

    if (!canEditDetails(row)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', padding: '0 8px' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shown}</span>
        </div>
      );
    }

    const edited = isEdited(row, field);
    const isActive = activeCell?.key === row.row_key && activeCell.field === field;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0 8px',
          backgroundColor: edited ? `${COLORS.teal}15` : 'transparent',
          cursor: 'pointer',
          borderRadius: '4px',
        }}
        onClick={(e) => { e.stopPropagation(); setActiveCell({ key: row.row_key, field }); }}
        onMouseDown={isActive ? (e) => e.stopPropagation() : undefined}
        title="Click to edit"
      >
        {isActive ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
            {edited && <EditedDot />}
            <input
              type={type}
              step={field === 'price' ? '0.01' : undefined}
              min={type === 'number' ? 0 : undefined}
              aria-label={`${field.replace(/_/g, ' ')} for ${row.product_code}`}
              value={display === null || display === undefined ? '' : String(display)}
              onChange={(e) => {
                const raw = e.target.value;
                setField(row, field, type === 'number' ? (raw === '' ? '' : Number(raw)) : raw);
              }}
              onBlur={() => setActiveCell(null)}
              autoFocus
              maxLength={field === 'sales_pricing_unit' ? 5 : undefined}
              onKeyDown={(e) => {
                if (type === 'number') blockNonNumericKeys(e);
                if (e.key === 'Enter' || e.key === 'Escape') setActiveCell(null);
              }}
              style={{ width: '100%', border: `1px solid ${COLORS.teal}`, borderRadius: '4px', padding: '4px 8px', fontSize: '14px' }}
            />
          </div>
        ) : (
          <span style={{ fontWeight: edited ? 'bold' : 'normal', color: edited ? COLORS.teal : 'inherit', display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
            {edited && <EditedDot />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shown}</span>
          </span>
        )}
      </div>
    );
  };

  const renderCategoryCell = (row: InventoryRow) => {
    if (!canEditDetails(row)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', padding: '0 8px' }}>
          {row.category_name || '-'}
        </div>
      );
    }

    const edited = isEdited(row, 'category_id');
    const isActive = activeCell?.key === row.row_key && activeCell.field === 'category_id';
    const currentId = valueOf(row, 'category_id');
    const displayName = edited ? (pending[row.row_key]?.fields.category_name ?? row.category_name) : row.category_name;
    const categories = categoryCache[String(row.ovid)] || [];

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0 8px',
          backgroundColor: edited ? `${COLORS.teal}15` : 'transparent',
          cursor: 'pointer',
          borderRadius: '4px',
        }}
        onClick={(e) => { e.stopPropagation(); setActiveCell({ key: row.row_key, field: 'category_id' }); }}
        onMouseDown={isActive ? (e) => e.stopPropagation() : undefined}
        title="Click to edit"
      >
        {isActive ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
            {edited && <EditedDot />}
            <select
              aria-label={`Category for ${row.product_code}`}
              value={currentId ?? ''}
              onChange={(e) => {
                setField(row, 'category_id', Number(e.target.value));
                setField(row, 'category_name', e.target.options[e.target.selectedIndex]?.text || '');
              }}
              onBlur={() => setActiveCell(null)}
              autoFocus
              style={{ width: '100%', padding: '4px 8px', border: `1px solid ${COLORS.teal}`, borderRadius: '4px', fontSize: '14px' }}
            >
              <option value="" disabled>{categories.length ? 'Select a Category' : 'Loading…'}</option>
              {categories.map((cat: any) => (
                <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
              ))}
            </select>
          </div>
        ) : (
          <span style={{ fontWeight: edited ? 'bold' : 'normal', color: edited ? COLORS.teal : 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {edited && <EditedDot />}
            {displayName || '-'}
          </span>
        )}
      </div>
    );
  };

  const qtyHeader = (title: string) => () => (
    <div style={{ ...editableHeaderWrapperStyle, ...(isEditAllMode ? { backgroundColor: `${COLORS.teal}08` } : {}) }}>
      <div style={editableHeaderTitleRowStyle}>
        <span style={editableHeaderTitleStyle}>{title}</span>
        <span style={editableHeaderHintStyle} title={isEditAllMode ? 'All rows are editable' : 'Click cell to edit'}>✎</span>
      </div>
    </div>
  );

  const searchHeader = (field: string, headerName: string) => () => (
    <SearchableHeader
      field={field}
      headerName={headerName}
      searchValue={columnSearches[field]}
      onSearchChange={handleColumnSearch}
      onClearSearch={handleClearColumnSearch}
    />
  );

  const columns: ColumnDef[] = [
    {
      field: 'bins',
      headerName: 'Bins',
      width: 56,
      sortable: false,
      filterable: false,
      defaultFirst: true,
      renderHeader: () => <></>,
      renderCell: (params: any) => (
        <BinToggleCell
          enabled={Number(params.row.binlocation_flag) === 1}
          expanded={expandedBinRowId === params.row.row_key}
          canEdit={canEditBins}
          onToggle={() => toggleBinRow(params.row.row_key)}
        />
      ),
    },
    { field: 'CustomerName', headerName: 'Account', width: 180, sortable: true, renderHeader: searchHeader('CustomerName', 'Account') },
    { field: 'location_name', headerName: 'Location', width: 200, sortable: true, renderHeader: searchHeader('location_name', 'Location') },
    {
      field: 'product_code',
      headerName: 'Part No.',
      width: 168,
      sortable: true,
      renderCell: (params: any) => renderDetailCell(params.row, 'product_code'),
      renderHeader: searchHeader('product_code', 'Part No.'),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 420,
      sortable: true,
      renderCell: (params: any) => renderDetailCell(params.row, 'description'),
      renderHeader: searchHeader('description', 'Description'),
    },
    {
      field: 'category_name',
      headerName: 'Category',
      width: 160,
      sortable: true,
      renderCell: (params: any) => renderCategoryCell(params.row),
      renderHeader: searchHeader('category_name', 'Category'),
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 84,
      sortable: true,
      renderCell: (params: any) => renderDetailCell(params.row, 'price', 'number'),
    },
    {
      field: 'sales_pricing_unit',
      headerName: 'UOM',
      width: 84,
      sortable: true,
      renderCell: (params: any) => renderDetailCell(params.row, 'sales_pricing_unit'),
      renderHeader: searchHeader('sales_pricing_unit', 'UOM'),
    },
    { field: 'quantity', headerName: 'QTY', width: 92, sortable: true, renderCell: (p: any) => renderQuantityCell(p.row, 'quantity'), renderHeader: qtyHeader('QTY') },
    { field: 'quantity_minimum', headerName: 'MIN QTY', width: 92, sortable: true, renderCell: (p: any) => renderQuantityCell(p.row, 'quantity_minimum'), renderHeader: qtyHeader('MIN QTY') },
    { field: 'quantity_maximum', headerName: 'MAX QTY', width: 92, sortable: true, renderCell: (p: any) => renderQuantityCell(p.row, 'quantity_maximum'), renderHeader: qtyHeader('MAX QTY') },
    {
      field: 'percent_full',
      headerName: '% Full',
      width: 120,
      sortable: true,
      filterable: false,
      type: 'number',
      align: 'left',
      valueGetter: (_value: any, row: any) => {
        const qty = row.quantity ?? 0;
        const max = row.quantity_maximum ?? 0;
        if (max <= 0) return null;
        return Math.min(100, Math.round((qty / max) * 100));
      },
      renderCell: (params: any) => {
        const qty = params.row.quantity ?? 0;
        const max = params.row.quantity_maximum ?? 0;
        const min = params.row.quantity_minimum ?? 0;
        if (max <= 0) return <div style={{ color: '#888', fontSize: 12 }}>—</div>;
        const pct = typeof params.value === 'number' ? params.value : Math.min(100, Math.round((qty / max) * 100));
        const barColor = qty < min ? '#E87722' : pct < 50 ? '#9E9E9E' : '#008490';
        return (
          <div style={{ width: '100%' }}>
            <div style={{ position: 'relative', width: '100%', height: 22, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 4, transition: 'width 0.3s ease' }} />
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: pct >= 40 ? '#fff' : '#333' }}>
                {pct}%
              </span>
            </div>
          </div>
        );
      },
    },
    {
      field: 'reorder_quantity',
      headerName: 'Reorder Qty.',
      width: 114,
      sortable: true,
      type: 'number',
      align: 'left',
      valueGetter: (_value: any, row: any) => {
        const raw = row.reorder_quantity ?? ((row.quantity_maximum ?? 0) - (row.quantity ?? 0));
        return Math.max(0, Number(raw) || 0);
      },
      renderCell: (params: any) => <div>{typeof params.value === 'number' ? params.value : 0}</div>,
    },
    {
      field: 'item_type',
      headerName: 'Type',
      width: 118,
      sortable: true,
      filterable: false,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <ItemTypeChip type={params.row.item_type} />
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const row = params.row as InventoryRow;
        const slot: React.CSSProperties = { width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '100%' }}>
            <div style={slot}>
              {canEditQuantities(row) && (
                <IconActionButton onClick={() => setActiveEditRowId(row.row_key)} title="Edit">
                  <PencilIcon />
                </IconActionButton>
              )}
            </div>
            <div style={slot}>
              {canDelete(row) && (
                <IconActionButton onClick={() => setDeleteTarget(row)} $variant="danger" title="Delete">
                  <Trash2Icon />
                </IconActionButton>
              )}
            </div>
            {Number(row.binlocation_flag) === 1 && (
              <IconActionButton onClick={() => toggleBinRow(row.row_key, 'manage')} title="Manage Bin">
                <Boxes />
              </IconActionButton>
            )}
          </div>
        );
      },
    },
  ];

  const editedCount = Object.keys(pending).length;

  const deleteMessage = deleteTarget?.item_type === 'non-abatix'
    ? `${deleteTarget.product_code} will be removed from inventory. You can restore it later from Non-ABATIX → Deleted Items.`
    : 'This item will be permanently removed from inventory. This action cannot be undone.';

  return (
    <PageContainer>
      <Header title="Inventory" count={totalRows} hideCount={false} />
      <DataTable
        data={rows}
        columns={columns}
        pageKey="inventory-v2"
        idField="row_key"
        defaultColumnVisibility={{ CustomerName: false, category_name: false }}
        loading={loading || saving}
        serverSidePagination={true}
        page={page}
        rowCount={totalRows}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={pageSize}
        hideColumnFilters={true}
        hideSearchBar={true}
        serverSideSort={true}
        defaultSort={[{ field: 'product_code', sort: 'asc' }]}
        onSortModelChange={handleSortChange}
        onEditAll={() => setIsEditAllMode(true)}
        editAllTooltip="Edit QTY, Min and Max for all rows on this page"
        isEditAllMode={isEditAllMode}
        isRowEditMode={!!activeEditRowId}
        hasEdits={editedCount > 0}
        editedCount={editedCount}
        onSaveChanges={handleSaveChanges}
        onCancelChanges={resetEditing}
        expandedRowId={expandedBinRowId}
        renderExpandedRow={(row: any) => (
          <BinDetailsPanel
            endpoint={row.item_type === 'non-abatix'
              ? `/api/non-abatix/inventory/${row.source_id}/bins`
              : `/api/inventory/${row.source_id}/bins`}
            quantityOnHand={Number(row.quantity ?? 0)}
            mode="manage"
            canEdit={true}
            isConsignment={row.item_type === 'abatix' && !!row.consignment_flag}
            onClose={() => setExpandedBinRowId(null)}
            onSaved={refetch}
          />
        )}
      >
        <LocationSelect
          ovs={ovs}
          selectedOvs={selectedOvs}
          onChange={(value) => { setSelectedOvs(value === null || value === undefined ? '' : String(value)); setPage(0); }}
          setDefault={true}
          defaultName="All Locations"
        />
        <ItemTypeToggle value={itemType} onChange={handleTypeChange} />
      </DataTable>

      {deleteTarget && (
        <ConfirmationModal
          title="Delete Item?"
          message={deleteMessage}
          isLoading={isDeleting}
          loadingText="Deleting..."
          onConfirm={async () => {
            if (isDeleting || !deleteTarget) return;
            setIsDeleting(true);
            try {
              const url = deleteTarget.item_type === 'non-abatix'
                ? `/api/non-abatix/inventory/${deleteTarget.source_id}`
                : `/api/inventory/${deleteTarget.source_id}`;
              const response = await apiClient(url, { method: 'DELETE' });
              if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                showSnackbar(err.error || 'Failed to delete', 'error', 'Something went wrong!');
              } else {
                showSnackbar('Item deleted', 'success');
                setPending((prev) => {
                  const next = { ...prev };
                  delete next[deleteTarget.row_key];
                  return next;
                });
                refetch();
                setDeleteTarget(null);
              }
            } finally {
              setIsDeleting(false);
            }
          }}
          onCancel={() => { if (!isDeleting) setDeleteTarget(null); }}
        />
      )}
    </PageContainer>
  );
};

export default InventoryPage;
